// /Frontend/src/pages/AdminPurchases.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Server, LogOut, Settings, ArrowLeft, Plus, X,
  AlertCircle, Building2, CreditCard, CheckCircle,
  XCircle, PauseCircle, Package, TrendingUp, DollarSign,
  ChevronDown, Edit2, Trash2, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AdminNav from '../components/AdminNav';

// ─── Modal wrapper ────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <div className={`relative w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active:    { color: 'bg-green-500/10 text-green-400 border-green-500/20',    icon: CheckCircle,   label: 'Active' },
    paused:    { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: PauseCircle,   label: 'Paused' },
    cancelled: { color: 'bg-red-500/10 text-red-400 border-red-500/20',          icon: XCircle,       label: 'Cancelled' },
    pending:   { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20',       icon: RefreshCw,     label: 'Pending' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${s.color}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </span>
  );
};

// ─── Plan badge ───────────────────────────────────────────────────────────────
const PlanBadge = ({ slug }) => {
  const map = {
    'web-hosting':       'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    'namespace':         'bg-blue-500/10 text-blue-400 border-blue-500/20',
    'managed-app':       'bg-purple-500/10 text-purple-400 border-purple-500/20',
    'dedicated-cluster': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${map[slug] || 'bg-slate-500/10 text-slate-400 border-slate-500/20'} capitalize`}>
      {slug?.replace(/-/g, ' ')}
    </span>
  );
};

// ─── Purchase form ────────────────────────────────────────────────────────────
const PurchaseForm = ({ plans, companies, onSubmit, onCancel, loading, error }) => {
  const [form, setForm] = useState({
    company_id: '',
    plan_id: '',
    billing_cycle: 'monthly',
    quantity: 1,
    price_override: '',
    notes: '',
    addons: [],
  });

  const selectedPlan = plans.find(p => p.id === form.plan_id);
  const isCustom = selectedPlan?.is_custom_price;

  const toggleAddon = (addonId) => {
    setForm(prev => {
      const exists = prev.addons.find(a => a.addon_id === addonId);
      if (exists) {
        return { ...prev, addons: prev.addons.filter(a => a.addon_id !== addonId) };
      }
      return { ...prev, addons: [...prev.addons, { addon_id: addonId, quantity: 1 }] };
    });
  };

  const updateAddonQty = (addonId, quantity) => {
    setForm(prev => ({
      ...prev,
      addons: prev.addons.map(a => a.addon_id === addonId ? { ...a, quantity: parseInt(quantity) || 1 } : a),
    }));
  };

  // Calculate live price total
  const calcTotal = () => {
    if (!selectedPlan) return 0;
    const base = isCustom
      ? parseFloat(form.price_override || 0)
      : parseFloat(selectedPlan.price_monthly);
    let addonTotal = 0;
    for (const item of form.addons) {
      const def = selectedPlan.addons?.find(a => a.id === item.addon_id);
      if (def) addonTotal += parseFloat(def.price_monthly) * (item.quantity || 1);
    }
    return ((base + addonTotal) * (form.quantity || 1)).toFixed(2);
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form); }} className="space-y-5">
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Company */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Company *</label>
        <select
          required value={form.company_id}
          onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
        >
          <option value="">Select a company...</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Plan */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Service Plan *</label>
        <select
          required value={form.plan_id}
          onChange={e => setForm(p => ({ ...p, plan_id: e.target.value, addons: [], price_override: '' }))}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
        >
          <option value="">Select a plan...</option>
          {plans.map(p => (
            <option key={p.id} value={p.id}>
              {p.name} {p.is_custom_price ? '— Custom pricing' : `— $${p.price_monthly}/mo`}
            </option>
          ))}
        </select>
      </div>

      {/* Selected plan info */}
      {selectedPlan && (
        <div className="p-3 bg-slate-800/60 border border-slate-700 rounded-lg text-sm text-slate-300 space-y-1">
          {selectedPlan.storage_gb && <p>💾 {selectedPlan.storage_gb} GB storage</p>}
          {selectedPlan.ram_gb     && <p>🧠 {selectedPlan.ram_gb} GB RAM</p>}
          {selectedPlan.vcpu       && <p>⚡ {selectedPlan.vcpu} vCPU</p>}
          {selectedPlan.description && <p className="text-slate-400 text-xs mt-1">{selectedPlan.description}</p>}
        </div>
      )}

      {/* Custom price override for Enterprise */}
      {isCustom && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Monthly Price (custom) *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              type="number" min="0" step="0.01" required
              value={form.price_override}
              onChange={e => setForm(p => ({ ...p, price_override: e.target.value }))}
              placeholder="0.00"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-7 pr-3 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors text-sm"
            />
          </div>
        </div>
      )}

      {/* Addons */}
      {selectedPlan?.addons?.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Add-ons</label>
          <div className="space-y-2">
            {selectedPlan.addons.map(addon => {
              const selected = form.addons.find(a => a.addon_id === addon.id);
              return (
                <div key={addon.id} className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${selected ? 'bg-blue-500/10 border-blue-500/30' : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'}`}>
                  <div className="flex items-center gap-3" onClick={() => toggleAddon(addon.id)}>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected ? 'bg-blue-500 border-blue-500' : 'border-slate-600'}`}>
                      {selected && <CheckCircle className="w-3 h-3 text-white" />}
                    </div>
                    <div>
                      <p className="text-sm text-white">{addon.name}</p>
                      <p className="text-xs text-slate-400">${addon.price_monthly}/mo · {addon.unit}</p>
                    </div>
                  </div>
                  {selected && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Qty:</span>
                      <input
                        type="number" min="1" max="20"
                        value={selected.quantity}
                        onChange={e => updateAddonQty(addon.id, e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="w-14 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Billing cycle + Quantity */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Billing Cycle</label>
          <select
            value={form.billing_cycle}
            onChange={e => setForm(p => ({ ...p, billing_cycle: e.target.value }))}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">Quantity</label>
          <input
            type="number" min="1" max="100"
            value={form.quantity}
            onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors text-sm"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Notes</label>
        <textarea
          rows={2} value={form.notes}
          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          placeholder="Optional notes about this subscription..."
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors text-sm resize-none"
        />
      </div>

      {/* Price preview */}
      {selectedPlan && (
        <div className="p-3 bg-gradient-to-r from-blue-500/10 to-cyan-400/10 border border-blue-500/20 rounded-lg flex items-center justify-between">
          <span className="text-sm text-slate-300">Total per billing cycle</span>
          <span className="text-xl font-bold text-white">${calcTotal()}</span>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="submit" disabled={loading}
          className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-medium py-2.5 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
        >
          {loading
            ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Creating...</span></>
            : <><CreditCard className="w-4 h-4" /><span>Create Subscription</span></>
          }
        </button>
        <button
          type="button" onClick={onCancel}
          className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const AdminPurchases = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans]                 = useState([]);
  const [companies, setCompanies]         = useState([]);
  const [stats, setStats]                 = useState({ mrr: 0, active_subscriptions: 0 });
  const [loading, setLoading]             = useState(true);
  const [showNewModal, setShowNewModal]   = useState(false);
  const [formLoading, setFormLoading]     = useState(false);
  const [formError, setFormError]         = useState('');
  const [statusFilter, setStatusFilter]   = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchAll = async () => {
    try {
      const [subsRes, plansRes, companiesRes, statsRes] = await Promise.all([
        api.get('/admin/subscriptions'),
        api.get('/plans'),
        api.get('/admin/companies'),
        api.get('/admin/subscriptions/stats/mrr'),
      ]);
      setSubscriptions(subsRes.data.subscriptions || []);
      setPlans(plansRes.data.plans || []);
      setCompanies(companiesRes.data.companies || []);
      setStats(statsRes.data || { mrr: 0, active_subscriptions: 0 });
    } catch (err) {
      console.error('Error fetching purchases data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Create subscription ─────────────────────────────────────────────────────
  const handleCreate = async (form) => {
    setFormError('');
    setFormLoading(true);
    try {
      await api.post('/admin/subscriptions', form);
      setShowNewModal(false);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create subscription');
    } finally {
      setFormLoading(false);
    }
  };

  // ── Update status ───────────────────────────────────────────────────────────
  const handleStatusChange = async (id, status) => {
    setActionLoading(id);
    try {
      await api.post(`/admin/subscriptions/${id}/status`, { status });
      setSubscriptions(prev => prev.map(s => s.id === id ? { ...s, status } : s));
      fetchAll(); // refresh MRR stats
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Delete subscription (cancelled only) ───────────────────────────────────
  const handleDelete = async (id) => {
    setActionLoading(id);
    try {
      await api.delete(`/admin/subscriptions/${id}`);
      setSubscriptions(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting subscription:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // ── Filter ──────────────────────────────────────────────────────────────────
  const filtered = statusFilter === 'all'
    ? subscriptions
    : subscriptions.filter(s => s.status === statusFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <AdminNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Page title + new button ── */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold">Purchases & Subscriptions</h2>
            <p className="text-slate-400 text-sm mt-1">Manage service plans and company subscriptions</p>
          </div>
          <button
            onClick={() => { setFormError(''); setShowNewModal(true); }}
            className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-medium rounded-lg transition-all text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>New Subscription</span>
          </button>
        </div>

        {/* ── Stats row ── */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-400" />
              </div>
              <span className="text-2xl font-bold">${stats.mrr}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Monthly Recurring Revenue</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <span className="text-2xl font-bold">{stats.active_subscriptions}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Active Subscriptions</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-400" />
              </div>
              <span className="text-2xl font-bold">{plans.length}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Available Plans</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-cyan-400" />
              </div>
              <span className="text-2xl font-bold">{new Set(subscriptions.map(s => s.company_id)).size}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Subscribed Companies</h3>
          </div>
        </div>

        {/* ── Plan catalog ── */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Service Plans</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map(plan => (
              <div key={plan.id} className="bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-xl p-5 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-white text-sm">{plan.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{plan.slug}</p>
                  </div>
                  <span className="text-lg font-bold text-white">
                    {plan.is_custom_price ? 'Custom' : `$${plan.price_monthly}`}
                    {!plan.is_custom_price && <span className="text-xs font-normal text-slate-400">/mo</span>}
                  </span>
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                  {plan.storage_gb && <p>💾 {plan.storage_gb} GB storage</p>}
                  {plan.ram_gb     && <p>🧠 {plan.ram_gb} GB RAM</p>}
                  {plan.vcpu       && <p>⚡ {plan.vcpu} vCPU</p>}
                </div>
                {plan.addons?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">{plan.addons.length} add-ons available</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Subscriptions table ── */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
            <h3 className="font-semibold text-white">All Subscriptions</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Filter:</span>
              {['all', 'active', 'paused', 'cancelled', 'pending'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors capitalize ${
                    statusFilter === s
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <CreditCard className="w-12 h-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-400 mb-1">No subscriptions yet</p>
              <p className="text-slate-500 text-sm mb-6">Create a subscription to assign a service plan to a company</p>
              <button
                onClick={() => { setFormError(''); setShowNewModal(true); }}
                className="px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
              >
                Create first subscription
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left">
                    <th className="px-6 py-3 text-slate-400 font-medium">Company</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Plan</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Status</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Billing</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Total</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Next Billing</th>
                    <th className="px-6 py-3 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(sub => (
                    <tr key={sub.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-blue-400" />
                          </div>
                          <span className="font-medium text-white">{sub.company_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <PlanBadge slug={sub.plan_slug} />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={sub.status} />
                      </td>
                      <td className="px-6 py-4 text-slate-400 capitalize">{sub.billing_cycle}</td>
                      <td className="px-6 py-4 font-semibold text-white">${sub.price_total}</td>
                      <td className="px-6 py-4 text-slate-400">
                        {sub.next_billing_at
                          ? new Date(sub.next_billing_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {sub.status !== 'active' && (
                            <button
                              onClick={() => handleStatusChange(sub.id, 'active')}
                              disabled={actionLoading === sub.id}
                              className="px-2 py-1 text-xs bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 rounded transition-colors"
                            >
                              Activate
                            </button>
                          )}
                          {sub.status === 'active' && (
                            <button
                              onClick={() => handleStatusChange(sub.id, 'paused')}
                              disabled={actionLoading === sub.id}
                              className="px-2 py-1 text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/20 rounded transition-colors"
                            >
                              Pause
                            </button>
                          )}
                          {sub.status !== 'cancelled' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Cancel subscription for ${sub.company_name}?`)) {
                                  handleStatusChange(sub.id, 'cancelled');
                                }
                              }}
                              disabled={actionLoading === sub.id}
                              className="px-2 py-1 text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded transition-colors"
                            >
                              Cancel
                            </button>
                          )}
                          {sub.status === 'cancelled' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Permanently delete this subscription for ${sub.company_name}? This cannot be undone.`)) {
                                  handleDelete(sub.id);
                                }
                              }}
                              disabled={actionLoading === sub.id}
                              className="px-2 py-1 text-xs bg-slate-700 text-slate-400 border border-slate-600 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 rounded transition-colors flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              Delete
                            </button>
                          )}
                          {actionLoading === sub.id && (
                            <div className="w-4 h-4 border-2 border-slate-600 border-t-slate-400 rounded-full animate-spin ml-1" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* ── New subscription modal ── */}
      {showNewModal && (
        <Modal title="New Subscription" onClose={() => setShowNewModal(false)} wide>
          <PurchaseForm
            plans={plans}
            companies={companies}
            onSubmit={handleCreate}
            onCancel={() => setShowNewModal(false)}
            loading={formLoading}
            error={formError}
          />
        </Modal>
      )}
    </div>
  );
};

export default AdminPurchases;